import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import ReactDOM from 'react-dom';
import { useCart } from '../../../context/CartContext';
import { useAuth } from '../../../context/AuthContext';
import { useAccountPanel } from '../../../context/AccountPanelContext';
import { useWishlist } from '../../../context/WishlistContext';
import { useSearch } from '../../../context/SearchContext';
import { useSiteLogo } from '../../../context/SiteLogoContext';
import { useCategories } from '../../../hooks/useCategories';
import { BRAND } from '../../../config/brand';
import ApiImage from '../ApiImage';
import { IconArrowRight, IconSearch, IconClose, IconHeart, IconUser, IconTruck, IconChevronDown, IconShoppingBag, IconReturn } from '../Icons/Icons';
import './Navbar.css';

/* ─────────────────────────────────────────────
   NAV DATA — Dynamically fetched from API
   Categories are fetched from /api/categories?isActive=true
   Subcategories are fetched per category
───────────────────────────────────────────── */
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Build nav items from categories
 */
function buildNavItemsFromCategories(categories) {
  const navItems = [];

  // Add categories with mega menus — use /shop/{gender} routes
  categories.forEach((category) => {
    const basePath = `/shop/${category.slug}`;
    const subcategoryLinks = (category.subcategories || []).map((sub) => ({
      label: sub.name,
      path: `${basePath}/${sub.slug}`,
    }));

    navItems.push({
      id: category.slug,
      label: category.name,
      path: basePath,
      mega: {
        sections: [
          {
            heading: 'Discover',
            links: [
              { label: 'New Arrivals', path: `/new-arrivals?category=${category.slug}`, accent: true },
              { label: 'Bestsellers', path: `/bestsellers?category=${category.slug}` },
            ],
          },
          {
            heading: 'Categories',
            links: [
              { label: `Shop All ${category.name}`, path: basePath, bold: true },
              ...subcategoryLinks,
            ],
          },
        ],
        editorial: {
          image: category.image?.url || '/hero.png',
          fallback: '/hero.png',
          eyebrow: 'New In',
          headline: `Shop ${category.name}`,
          cta: 'Explore Now',
          path: basePath,
        },
      },
    });
  });

  // Add New Arrivals (static)
  navItems.push({
    id: 'new',
    label: 'New Arrivals',
    path: '/new-arrivals',
    accent: true,
    mega: null,
  });

  return navItems;
}

/* ─────────────────────────────────────────────
   MegaMenuPanel
   Rendered via ReactDOM.createPortal → body.
   Positioning relative to viewport, never
   clipped by any parent stacking context.
───────────────────────────────────────────── */
function MegaMenuPanel({ item, top, onClose, onMouseEnter }) {
  const { mega } = item;
  const ed = mega.editorial;

  return ReactDOM.createPortal(
    <div
      className="mega"
      style={{ top }}
      role="region"
      aria-label={`${item.label} navigation`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onClose}
    >
      <div className="mega__inner">
        {/* Link columns — only as many as there is data */}
        <div className="mega__cols">
          {mega.sections.map((section, si) => (
            <div key={si} className="mega__col">
              <p className="mega__col-head">{section.heading}</p>
              <ul className="mega__col-list">
                {section.links.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className={[
                        'mega__link',
                        link.bold ? 'mega__link--bold' : '',
                        link.accent ? 'mega__link--accent' : '',
                      ].filter(Boolean).join(' ')}
                      onClick={onClose}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Editorial image card */}
        {ed && (
          <Link
            to={ed.path}
            className="mega__ed"
            onClick={onClose}
          >
            <div className="mega__ed-img">
              <ApiImage
                src={ed.image}
                fallback={ed.fallback}
                alt={ed.headline}
                loading="lazy"
              />
            </div>
            <div className="mega__ed-body">
              <span className="mega__ed-eyebrow">{ed.eyebrow}</span>
              <span className="mega__ed-headline">{ed.headline}</span>
              <span className="mega__ed-cta">
                {ed.cta}
                <IconArrowRight size={12} />
              </span>
            </div>
          </Link>
        )}
      </div>
    </div>,
    document.body
  );
}

/* ─────────────────────────────────────────────
   SearchPortal — Advanced type-ahead
   Uses GET /api/search/suggestions (products, categories, keywords).
   Debounced 300ms, grouped dropdown, keyboard nav, recent searches.
───────────────────────────────────────────── */
const RECENT_SEARCHES_KEY = 'evora_recent_searches';
const RECENT_MAX = 5;
const DEBOUNCE_MS = 300;
const SUGGESTION_CACHE_MAX = 5;
const suggestionCache = new Map(); // q (lowercase) -> { data, ts }; evict oldest when > max

function loadRecentSearches() {
  try {
    const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

function saveRecentSearch(term) {
  const t = String(term).trim();
  if (!t) return;
  const recent = loadRecentSearches().filter((r) => r.toLowerCase() !== t.toLowerCase());
  recent.unshift(t);
  try {
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent.slice(0, RECENT_MAX)));
  } catch (_) {}
}

function SearchPortal({ top, onClose }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState({ products: [], categories: [], brands: [], keywords: [] });
  const [popularSearches, setPopularSearches] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);
  const listRef = useRef(null);

  const hasSuggestions =
    suggestions.products.length > 0 ||
    suggestions.categories.length > 0 ||
    suggestions.keywords.length > 0;
  const showDropdown = query.trim().length >= 2 && (loadingSuggestions || hasSuggestions);
  const showRecent = query.trim().length < 2 && recentSearches.length > 0;

  // Load popular + recent on open
  useEffect(() => {
    let cancelled = false;
    setRecentSearches(loadRecentSearches());
    const staticTerms = ['Round Neck', 'Polo', 'New Arrivals', 'Bestsellers'];
    fetch(`${API_URL}/categories`, { cache: 'no-store' })
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (cancelled) return;
        const list = [...staticTerms];
        const cats = data?.data ?? data?.categories ?? [];
        if (Array.isArray(cats)) {
          cats.forEach((c) => {
            if (c?.name && !list.includes(c.name)) list.push(c.name);
            (c.subcategories || []).forEach((s) => {
              if (s?.name && !list.includes(s.name)) list.push(s.name);
            });
          });
        }
        setPopularSearches(list.slice(0, 12));
      })
      .catch(() => setPopularSearches(staticTerms));
    return () => { cancelled = true; };
  }, []);

  // Debounced suggestions from /api/search/suggestions (300ms)
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setSuggestions({ products: [], categories: [], brands: [], keywords: [] });
      setHighlightIndex(-1);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoadingSuggestions(true);
      setHighlightIndex(-1);
      const cacheKey = q.toLowerCase().slice(0, 100);
      const cached = suggestionCache.get(cacheKey);
      if (cached && Date.now() - cached.ts < 60 * 1000) {
        setSuggestions({
          products: Array.isArray(cached.data?.products) ? cached.data.products : [],
          categories: Array.isArray(cached.data?.categories) ? cached.data.categories : [],
          brands: Array.isArray(cached.data?.brands) ? cached.data.brands : [],
          keywords: Array.isArray(cached.data?.keywords) ? cached.data.keywords : [],
        });
        setLoadingSuggestions(false);
        return;
      }
      fetch(`${API_URL}/search/suggestions?q=${encodeURIComponent(q)}`, {
        signal: controller.signal,
        cache: 'no-store',
      })
        .then((res) => (res.ok ? res.json() : Promise.reject(new Error('fetch'))))
        .then((data) => {
          if (controller.signal.aborted) return;
          const d = data?.data ?? data;
          const payload = {
            products: Array.isArray(d?.products) ? d.products : [],
            categories: Array.isArray(d?.categories) ? d.categories : [],
            brands: Array.isArray(d?.brands) ? d.brands : [],
            keywords: Array.isArray(d?.keywords) ? d.keywords : [],
          };
          if (suggestionCache.size >= SUGGESTION_CACHE_MAX) {
            const first = suggestionCache.keys().next().value;
            if (first !== undefined) suggestionCache.delete(first);
          }
          suggestionCache.set(cacheKey, { data: payload, ts: Date.now() });
          setSuggestions(payload);
        })
        .catch(() => {
          if (!controller.signal.aborted)
            setSuggestions({ products: [], categories: [], brands: [], keywords: [] });
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoadingSuggestions(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [query]);

  useEffect(() => {
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  const goSearch = (term) => {
    const t = String(term || query).trim();
    if (!t) return;
    saveRecentSearch(t);
    navigate(`/search?q=${encodeURIComponent(t)}`);
    onClose();
  };

  const goProduct = (slug) => {
    if (slug) navigate(`/product/${slug}`);
    onClose();
  };

  const goCategory = (slug) => {
    if (slug) navigate(`/shop/${slug}`);
    onClose();
  };

  // Flatten clickable items for keyboard nav: [0]=see all, then products, then categories, then keywords
  const flatItems = useMemo(() => {
    const q = query.trim();
    const items = [];
    if (q.length >= 2) items.push({ type: 'all', label: `See all results for "${q}"` });
    suggestions.products.forEach((p) => items.push({ type: 'product', slug: p.slug, name: p.name }));
    suggestions.categories.forEach((c) => items.push({ type: 'category', slug: c.slug, name: c.name }));
    suggestions.keywords.forEach((k) => items.push({ type: 'keyword', term: k.term || k }));
    return items;
  }, [query, suggestions]);

  const handleKey = (e) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIndex >= 0 && highlightIndex < flatItems.length) {
        const item = flatItems[highlightIndex];
        if (item.type === 'all') goSearch(query.trim());
        else if (item.type === 'product') goProduct(item.slug);
        else if (item.type === 'category') goCategory(item.slug);
        else if (item.type === 'keyword') goSearch(item.term);
      } else if (query.trim()) goSearch(query.trim());
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => (i < flatItems.length - 1 ? i + 1 : i));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => (i <= 0 ? -1 : i - 1));
      return;
    }
    setHighlightIndex(-1);
  };

  const productImage = (p) => p.image || p.images?.[0]?.url || p.images?.[0]?.secure_url;

  return ReactDOM.createPortal(
    <>
      <div className="search-bd" onClick={onClose} aria-hidden="true" />
      <div className="search-panel" style={{ top }} role="search" aria-label="Site search" aria-modal="true">
        <div className="search-panel__row">
          <IconSearch size={17} className="search-panel__ico" />
          <input
            ref={inputRef}
            type="search"
            className="search-panel__input"
            placeholder="Search styles, colours, collections…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setHighlightIndex(-1); }}
            onKeyDown={handleKey}
            aria-label="Search"
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
            aria-expanded={showDropdown || showRecent}
            aria-controls="search-suggestions-list"
            aria-describedby="search-related-heading"
          />
          {query && (
            <button
              className="search-panel__clear"
              onClick={() => { setQuery(''); inputRef.current?.focus(); }}
              aria-label="Clear"
            >
              <IconClose size={13} />
            </button>
          )}
          <button className="search-panel__go" onClick={() => goSearch(query.trim())}>
            Search
          </button>
          <button className="search-panel__close" onClick={onClose} aria-label="Close search">
            <IconClose size={15} />
          </button>
        </div>

        <div className="search-panel__suggestions-wrap" id="search-suggestions-list" ref={listRef}>
          {loadingSuggestions && query.trim().length >= 2 && (
            <div className="search-panel__suggestions-loading" aria-live="polite">Searching…</div>
          )}

          {!loadingSuggestions && showDropdown && (
            <div className="search-panel__suggestions-list">
              {query.trim().length >= 2 && (
                <button
                  type="button"
                  className={`search-panel__suggestion-item search-panel__suggestion-item--all${highlightIndex === 0 ? ' search-panel__suggestion-item--highlight' : ''}`}
                  onClick={() => goSearch(query.trim())}
                >
                  <IconSearch size={16} className="search-panel__suggestion-ico" />
                  <span className="search-panel__suggestion-text">See all results for &ldquo;{query.trim()}&rdquo;</span>
                </button>
              )}
              {suggestions.products.length > 0 && (
                <>
                  <span className="search-panel__suggestions-list-label">Products</span>
                  <ul className="search-panel__suggestions-ul" role="list">
                    {suggestions.products.map((p, idx) => {
                      const flatIdx = (query.trim().length >= 2 ? 1 : 0) + idx;
                      return (
                        <li key={p._id || p.slug || p.name}>
                          <button
                            type="button"
                            className={`search-panel__suggestion-item${highlightIndex === flatIdx ? ' search-panel__suggestion-item--highlight' : ''}`}
                            onClick={() => goProduct(p.slug)}
                          >
                            {(productImage(p)) && (
                              <ApiImage src={productImage(p)} fallback="/hero.png" alt="" className="search-panel__suggestion-img" />
                            )}
                            <span className="search-panel__suggestion-text">{p.name}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
              {suggestions.categories.length > 0 && (
                <>
                  <span className="search-panel__suggestions-list-label">Categories</span>
                  <ul className="search-panel__suggestions-ul" role="list">
                    {suggestions.categories.map((c, idx) => {
                      const base = (query.trim().length >= 2 ? 1 : 0) + suggestions.products.length;
                      const flatIdx = base + idx;
                      return (
                        <li key={c._id || c.slug}>
                          <button
                            type="button"
                            className={`search-panel__suggestion-item search-panel__suggestion-item--cat${highlightIndex === flatIdx ? ' search-panel__suggestion-item--highlight' : ''}`}
                            onClick={() => goCategory(c.slug)}
                          >
                            <span className="search-panel__suggestion-text">{c.name}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
              {suggestions.keywords.length > 0 && (
                <>
                  <span className="search-panel__suggestions-list-label">Suggested</span>
                  <ul className="search-panel__suggestions-ul" role="list">
                    {suggestions.keywords.map((k, idx) => {
                      const term = k.term || k;
                      const base = (query.trim().length >= 2 ? 1 : 0) + suggestions.products.length + suggestions.categories.length;
                      const flatIdx = base + idx;
                      return (
                        <li key={term}>
                          <button
                            type="button"
                            className={`search-panel__suggestion-item search-panel__suggestion-item--kw${highlightIndex === flatIdx ? ' search-panel__suggestion-item--highlight' : ''}`}
                            onClick={() => goSearch(term)}
                          >
                            <IconSearch size={14} className="search-panel__suggestion-ico" />
                            <span className="search-panel__suggestion-text">{term}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </div>
          )}

          {!loadingSuggestions && showRecent && (
            <div className="search-panel__suggestions-list">
              <span className="search-panel__suggestions-list-label">Recent</span>
              <ul className="search-panel__suggestions-ul" role="list">
                {recentSearches.map((r) => (
                  <li key={r}>
                    <button
                      type="button"
                      className="search-panel__suggestion-item search-panel__suggestion-item--kw"
                      onClick={() => goSearch(r)}
                    >
                      <IconSearch size={14} className="search-panel__suggestion-ico" />
                      <span className="search-panel__suggestion-text">{r}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="search-panel__suggests" id="search-related-heading">
          <span className="search-panel__suggests-label">Popular</span>
          {popularSearches.map((s) => (
            <button key={s} className="search-panel__chip" onClick={() => goSearch(s)}>
              {s}
            </button>
          ))}
        </div>
      </div>
    </>,
    document.body
  );
}

/* ─────────────────────────────────────────────
   MobileDrawer
   Portal. Smooth height animation on sub-menus.
   Body scroll locked while open.
───────────────────────────────────────────── */
function MobileDrawer({ onClose, user, logout, openPanel, wishlistCount, navItems = [], logoUrl, logoAlt }) {
  const location = useLocation();
  const currentPath = location.pathname;
  const [expanded, setExpanded] = useState(null);
  // Measure sub-menu heights so we can animate with CSS height transition
  const subRefs = useRef({});
  const [subHeights, setSubHeights] = useState({});

  useEffect(() => {
    const next = {};
    Object.entries(subRefs.current).forEach(([id, el]) => {
      if (el) next[id] = el.scrollHeight;
    });
    setSubHeights(next);
  }, []);

  const toggle = (id) => setExpanded((p) => (p === id ? null : id));

  return ReactDOM.createPortal(
    <>
      <div className="drawer-bd" onClick={onClose} aria-hidden="true" />
      <div
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        {/* Header */}
        <div className="drawer__head">
          <Link to="/" className="drawer__logo" onClick={onClose} aria-label={BRAND.name}>
            <ApiImage src={logoUrl} fallback="/evora-logo.png" alt={logoAlt || BRAND.name} width="140" height="48" />
          </Link>
          <button className="drawer__close" onClick={onClose} aria-label="Close menu">
            <IconClose size={16} />
          </button>
        </div>

        {/* Primary nav */}
        <nav className="drawer__nav" aria-label="Mobile navigation">
          {navItems.map((item) => (
            <div key={item.id} className="drawer__item">
              {item.mega ? (
                <>
                  <button
                    className={[
                      'drawer__link',
                      'drawer__link--expand',
                      expanded === item.id ? 'is-open' : '',
                      (currentPath === item.path || currentPath.startsWith(`${item.path}/`)) ? 'drawer__link--active' : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => toggle(item.id)}
                    aria-expanded={expanded === item.id}
                    aria-current={(currentPath === item.path || currentPath.startsWith(`${item.path}/`)) ? 'page' : undefined}
                  >
                    {item.label}
                    <IconChevronDown size={14} className="drawer__chevron" />
                  </button>

                  {/* Animated sub-panel */}
                  <div
                    className={`drawer__sub${expanded === item.id ? ' is-open' : ''}`}
                    style={{
                      height: expanded === item.id
                        ? (subHeights[item.id] ?? 'auto')
                        : 0,
                    }}
                    aria-hidden={expanded !== item.id}
                  >
                    <div
                      ref={(el) => { subRefs.current[item.id] = el; }}
                      className="drawer__sub-inner"
                    >
                      {/* Shop All link — primary CTA (Baymard: clear entry point) */}
                      <Link
                        to={item.path}
                        className={[
                          'drawer__sublink',
                          'drawer__sublink--all',
                          (currentPath === item.path) ? 'drawer__sublink--active' : '',
                        ].filter(Boolean).join(' ')}
                        onClick={onClose}
                        aria-current={currentPath === item.path ? 'page' : undefined}
                      >
                        Shop All {item.label}
                      </Link>
                      {/* Sections with headings for scannability (NN/g: distinct grouping) */}
                      {item.mega.sections.map((section, si) => (
                        <div key={si} className="drawer__sub-group">
                          <span className="drawer__sub-head" aria-hidden="true">{section.heading}</span>
                          <ul className="drawer__sub-list" role="list">
                            {section.links
                              .filter((l) => !l.bold)
                              .map((link) => (
                                <li key={link.path}>
                                  <Link
                                    to={link.path}
                                    className={[
                                      'drawer__sublink',
                                      link.accent ? 'drawer__sublink--accent' : '',
                                      (currentPath === link.path) ? 'drawer__sublink--active' : '',
                                    ].filter(Boolean).join(' ')}
                                    onClick={onClose}
                                    aria-current={currentPath === link.path ? 'page' : undefined}
                                  >
                                    {link.label}
                                  </Link>
                                </li>
                              ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <Link
                  to={item.path}
                  className={[
                    'drawer__link',
                    item.accent ? 'drawer__link--accent' : '',
                    (currentPath === item.path || currentPath.startsWith(`${item.path}/`)) ? 'drawer__link--active' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={onClose}
                  aria-current={(currentPath === item.path || currentPath.startsWith(`${item.path}/`)) ? 'page' : undefined}
                >
                  {item.label}
                </Link>
              )}
            </div>
          ))}
        </nav>

        <div className="drawer__divider" />

        {/* Account */}
        <div className="drawer__acct">
          {user ? (
            <>
              <Link to="/account" className="drawer__acct-link" onClick={onClose}>My Account</Link>
              <Link to="/account/orders" className="drawer__acct-link" onClick={onClose}>My Orders</Link>
              <Link to="/wishlist" className="drawer__acct-link" onClick={onClose}>
                Saved Items
                {wishlistCount > 0 && <span className="drawer__acct-badge">{wishlistCount}</span>}
              </Link>
              <button className="drawer__acct-link drawer__acct-btn" onClick={() => { logout(); onClose(); }}>Sign Out</button>
            </>
          ) : (
            <>
              <button className="drawer__acct-link" onClick={() => { openPanel(); onClose(); }}>Sign In</button>
              <button className="drawer__acct-link" onClick={() => { openPanel(); onClose(); }}>Create Account</button>
            </>
          )}
        </div>

        {/* Trust footer */}
        <div className="drawer__foot">
          <span className="drawer__trust">
            <IconTruck size={11} />
            Free shipping on all orders
          </span>
          <span className="drawer__trust">
            <IconReturn size={11} />
            Returns for size &amp; defective only
          </span>
        </div>
      </div>
    </>,
    document.body
  );
}

/* ─────────────────────────────────────────────
   Navbar — main component
───────────────────────────────────────────── */
function Navbar() {
  const location = useLocation();
  const { getCartCount, openCart } = useCart();
  const { user, logout } = useAuth();
  const { openPanel } = useAccountPanel();
  const { wishlistCount, openWishlistDrawer } = useWishlist();
  const { resolvedLogoUrl, logoAlt } = useSiteLogo();
  const cartCount = getCartCount();

  // ── State ──
  const [navItems, setNavItems] = useState([
    // Fallback static items while loading or if API fails
    {
      id: 'men',
      label: 'Men',
      path: '/shop/men',
      mega: {
        sections: [
          {
            heading: 'Discover',
            links: [
              { label: 'New Arrivals', path: '/new-arrivals?category=men', accent: true },
              { label: 'Bestsellers', path: '/bestsellers?category=men' },
            ],
          },
          {
            heading: 'Categories',
            links: [
              { label: 'Shop All Men', path: '/shop/men', bold: true },
              { label: 'Round Neck', path: '/shop/men/round-neck' },
              { label: 'Polo', path: '/shop/men/polo' },
            ],
          },
        ],
        editorial: {
          image: '/men-editorial.jpg',
          fallback: '/hero.png',
          eyebrow: 'New In',
          headline: 'Shop Men',
          cta: 'Explore Now',
          path: '/shop/men',
        },
      },
    },
    {
      id: 'women',
      label: 'Women',
      path: '/shop/women',
      mega: {
        sections: [
          {
            heading: 'Discover',
            links: [
              { label: 'New Arrivals', path: '/new-arrivals?category=women', accent: true },
              { label: 'Bestsellers', path: '/bestsellers?category=women' },
            ],
          },
          {
            heading: 'Categories',
            links: [
              { label: 'Shop All Women', path: '/shop/women', bold: true },
              { label: 'Round Neck', path: '/shop/women/round-neck' },
              { label: 'Polo', path: '/shop/women/polo' },
            ],
          },
        ],
        editorial: {
          image: '/women-editorial.jpg',
          fallback: '/hero.png',
          eyebrow: 'New In',
          headline: 'Shop Women',
          cta: 'Explore Now',
          path: '/shop/women',
        },
      },
    },
    { id: 'new', label: 'New Arrivals', path: '/new-arrivals', accent: true, mega: null },
  ]);
  const [isScrolled, setIsScrolled] = useState(
    () => typeof window !== 'undefined' ? window.scrollY > 10 : false
  );
  const [isLight, setIsLight] = useState(false); // white text mode
  const [activeMenu, setActiveMenu] = useState(null);
  const { isSearchOpen, openSearch, closeSearch } = useSearch();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [cartPulse, setCartPulse] = useState(false);
  const [wishPulse, setWishPulse] = useState(false);

  // ── Categories with cache + retry (avoids 429 and failed-to-load) ──
  const { categories } = useCategories({ enabled: true });
  useEffect(() => {
    if (Array.isArray(categories) && categories.length > 0) {
      setNavItems(buildNavItemsFromCategories(categories));
    }
  }, [categories]);

  // ── Refs ──
  const headerRef = useRef(null);
  const menuTimer = useRef(null);
  const prevCart = useRef(cartCount);
  const prevWish = useRef(wishlistCount);

  /* ── Compute pixel bottom of navbar for portal top offset ── */
  const getHeaderBottom = useCallback(() => {
    return headerRef.current?.getBoundingClientRect().bottom ?? 64;
  }, []);

  /* ── Scroll ── */
  useEffect(() => {
    const fn = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  /* ── Auto dark-hero detection via IntersectionObserver ──
     Place data-dark-hero on any section that has a dark background
     directly below the navbar. No prop. No coupling. */
  useEffect(() => {
    const hero = document.querySelector('[data-dark-hero]');
    if (!hero) { setIsLight(false); return; }

    const io = new IntersectionObserver(
      ([entry]) => {
        setIsLight(entry.isIntersecting && window.scrollY < 80);
      },
      { rootMargin: '-56px 0px 0px 0px', threshold: 0.05 }
    );
    io.observe(hero);
    return () => io.disconnect();
  }, [location.pathname]);

  // Also reset isLight when scrolled
  useEffect(() => {
    if (isScrolled) setIsLight(false);
  }, [isScrolled]);

  /* ── Badge pulse on count increase ── */
  useEffect(() => {
    if (cartCount > prevCart.current) {
      setCartPulse(true);
      setTimeout(() => setCartPulse(false), 600);
    }
    prevCart.current = cartCount;
  }, [cartCount]);

  useEffect(() => {
    if (wishlistCount > prevWish.current) {
      setWishPulse(true);
      setTimeout(() => setWishPulse(false), 600);
    }
    prevWish.current = wishlistCount;
  }, [wishlistCount]);

  /* ── Body scroll lock ── */
  useEffect(() => {
    document.body.classList.toggle('no-scroll', (isMobileOpen || isSearchOpen));
    return () => { document.body.classList.remove('no-scroll'); };
  }, [isMobileOpen, isSearchOpen]);

  /* ── Close everything on route change ── */
  useEffect(() => {
    setActiveMenu(null);
    closeSearch();
    setIsMobileOpen(false);
  }, [location.pathname]);

  /* ── Escape ── */
  useEffect(() => {
    const fn = (e) => {
      if (e.key !== 'Escape') return;
      setActiveMenu(null);
      closeSearch();
      setIsMobileOpen(false);
    };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, []);

  /* ── Mega hover handlers ── */
  const openMenu = useCallback((id) => {
    clearTimeout(menuTimer.current);
    setActiveMenu(id);
  }, []);

  const startClose = useCallback(() => {
    menuTimer.current = setTimeout(() => setActiveMenu(null), 150);
  }, []);

  const cancelClose = useCallback(() => {
    clearTimeout(menuTimer.current);
  }, []);

  /* ── Build className ── */
  const cls = [
    'nav',
    isScrolled ? 'nav--scrolled' : 'nav--top',
    isLight && !isScrolled ? 'nav--light' : '',
    isMobileOpen ? 'nav--open' : '',
  ].filter(Boolean).join(' ');

  // Highlight current scope in main navigation (Baymard: users need “where am I”)
  const isScopeActive = useCallback((path) => {
    if (!path) return false;
    const p = location.pathname;
    return p === path || p.startsWith(`${path}/`);
  }, [location.pathname]);

  return (
    <>
      <header ref={headerRef} className={cls} role="banner">
        <div className="nav__bar">

          {/*
            ── LEFT SLOT (col 1) ──
            Mobile:  hamburger only → justify-start
            Desktop: nav links → justify-start
            Hamburger is DOM-first so it's in the correct
            grid column. Nav links are display:none on mobile.
          */}
          <div className="nav__left-slot">
            {/* Hamburger — only shown on mobile via CSS */}
            <button
              className={`nav__ham${isMobileOpen ? ' is-open' : ''}`}
              onClick={() => setIsMobileOpen(!isMobileOpen)}
              aria-label={isMobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileOpen}
            >
              <span aria-hidden="true" />
              <span aria-hidden="true" />
              <span aria-hidden="true" />
            </button>

            {/* Desktop nav links — hidden on mobile via CSS */}
            <nav className="nav__left" aria-label="Primary navigation">
              {navItems.map((item) =>
                item.mega ? (
                  <Link
                    key={item.id}
                    className={[
                      'nav__link',
                      item.accent ? 'nav__link--accent' : '',
                      activeMenu === item.id ? 'nav__link--on' : '',
                      isScopeActive(item.path) ? 'nav__link--active' : '',
                    ].filter(Boolean).join(' ')}
                    to={item.path}
                    onMouseEnter={() => openMenu(item.id)}
                    onMouseLeave={startClose}
                    onFocus={() => openMenu(item.id)}
                    onClick={() => setActiveMenu(null)}
                    aria-haspopup="true"
                    aria-expanded={activeMenu === item.id}
                    aria-current={isScopeActive(item.path) ? 'page' : undefined}
                  >
                    {item.label}
                    <span className="nav__link-bar" aria-hidden="true" />
                  </Link>
                ) : (
                  <Link
                    key={item.id}
                    to={item.path}
                    className={[
                      'nav__link',
                      item.accent ? 'nav__link--accent' : '',
                      isScopeActive(item.path) ? 'nav__link--active' : '',
                    ].filter(Boolean).join(' ')}
                    onMouseEnter={() => setActiveMenu(null)}
                    aria-current={isScopeActive(item.path) ? 'page' : undefined}
                  >
                    {item.label}
                    <span className="nav__link-bar" aria-hidden="true" />
                  </Link>
                )
              )}
            </nav>
          </div>

          {/* CENTER — logo, mathematically centred in col 2 */}
          <Link to="/" className="nav__logo" aria-label={`${BRAND.name} — Home`}>
            <ApiImage
              src={resolvedLogoUrl}
              fallback="/evora-logo.png"
              alt={logoAlt || BRAND.name}
              className="nav__logo-img"
              width="200"
              height="72"
            />
          </Link>

          {/* RIGHT SLOT (col 3) — icons, justify-end */}
          <div className="nav__right">
            {/* Search — always visible */}
            <button
              className={`nav__btn${isSearchOpen ? ' nav__btn--on' : ''}`}
              onClick={() => { isSearchOpen ? closeSearch() : openSearch(); setActiveMenu(null); }}
              aria-label="Search"
              aria-expanded={isSearchOpen}
            >
              <IconSearch size={18} />
            </button>

            {/* Account — desktop only */}
            <button
              className="nav__btn nav__btn--deskonly"
              onClick={() => openPanel()}
              aria-label={user ? 'My account' : 'Sign in'}
            >
              {user?.firstName || user?.name ? (
                <span className="nav__avatar" aria-hidden="true">
                  {(user.firstName || user.name).charAt(0).toUpperCase()}
                </span>
              ) : (
                <IconUser size={18} />
              )}
            </button>

            {/* Wishlist — desktop only */}
            <button
              className={`nav__btn nav__btn--badge nav__btn--deskonly${wishPulse ? ' nav__btn--pulse' : ''}`}
              onClick={() => openWishlistDrawer()}
              aria-label={`Wishlist${wishlistCount > 0 ? `, ${wishlistCount} items` : ''}`}
            >
              <IconHeart size={18} />
              {wishlistCount > 0 && (
                <span className="nav__badge" aria-hidden="true">
                  {wishlistCount > 9 ? '9+' : wishlistCount}
                </span>
              )}
            </button>

            {/* Cart — always visible */}
            <button
              className={`nav__btn nav__btn--badge nav__btn--cart${cartPulse ? ' nav__btn--pulse' : ''}`}
              onClick={() => openCart()}
              aria-label={`Bag${cartCount > 0 ? `, ${cartCount} items` : ''}`}
            >
              <IconShoppingBag size={18} />
              {cartCount > 0 && (
                <span className="nav__badge nav__badge--dark" aria-hidden="true">
                  {cartCount > 9 ? '9+' : cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* ── MEGA MENUS — portaled to body ── */}
      {navItems.filter((n) => n.mega && activeMenu === n.id).map((item) => (
        <MegaMenuPanel
          key={item.id}
          item={item}
          top={getHeaderBottom()}
          onClose={() => setActiveMenu(null)}
          onMouseEnter={cancelClose}
        />
      ))}

      {/* Mega backdrop */}
      {activeMenu && (
        <div
          className="nav__mega-bd"
          onMouseEnter={() => setActiveMenu(null)}
          aria-hidden="true"
        />
      )}

      {/* ── SEARCH — portaled to body ── */}
      {isSearchOpen && (
        <SearchPortal
          top={getHeaderBottom()}
          onClose={closeSearch}
        />
      )}

      {/* ── MOBILE DRAWER — portaled to body ── */}
      {isMobileOpen && (
        <MobileDrawer
          onClose={() => setIsMobileOpen(false)}
          user={user}
          logout={logout}
          openPanel={openPanel}
          wishlistCount={wishlistCount}
          navItems={navItems}
          logoUrl={resolvedLogoUrl}
          logoAlt={logoAlt}
        />
      )}
    </>
  );
}

export default Navbar;
